import { useState, useEffect } from "react";
import {
    Box,
    Card,
    Typography,
    TextField,
    Button,
    Grid,
    Stack,
    CircularProgress,
    Alert,
    Divider,
    MenuItem,
    Chip,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import { getProfile, updateProfile, changeMpin } from "../api";

const GENDER_OPTS = ["Male", "Female", "Other"];

/** Map backend snake_case profile to frontend camelCase */
function profileToForm(apiUser) {
    if (!apiUser) return {};
    return {
        id: apiUser.id,
        firstName: apiUser.first_name ?? apiUser.firstName ?? "",
        lastName: apiUser.last_name ?? apiUser.lastName ?? "",
        email: apiUser.email ?? "",
        mobileNumber: apiUser.mobile_number ?? apiUser.mobileNumber ?? "",
        gender: apiUser.gender ?? "",
        dateOfBirth: apiUser.date_of_birth ?? apiUser.dateOfBirth ?? "",
        address1: apiUser.address_1 ?? apiUser.address1 ?? "",
        address2: apiUser.address_2 ?? apiUser.address2 ?? "",
        city: apiUser.city ?? "",
        state: apiUser.state ?? "",
        pincode: apiUser.pincode ?? "",
        panCardNumber: apiUser.pan_card_number ?? apiUser.panCardNumber ?? "",
        aadharNumber: apiUser.aadhar_card_number ?? apiUser.aadharNumber ?? "",
        nameOnAadhar: apiUser.name_on_aadhar ?? apiUser.nameOnAadhar ?? "",
        bankAccountNumber:
            apiUser.bank_account_number ?? apiUser.bankAccountNumber ?? "",
        ifscCode: apiUser.ifsc_code ?? apiUser.ifscCode ?? "",
        roleName: apiUser.role_name ?? apiUser.roleName ?? "",
        isActive: apiUser.is_active ?? apiUser.isActive ?? true,
        depositLimit: apiUser.deposit_limit ?? apiUser.depositLimit ?? "",
        withdrawDailyLimit:
            apiUser.withdraw_daily_limit ?? apiUser.withdrawDailyLimit ?? "",
        createdAt: apiUser.created_at ?? apiUser.createdAt,
    };
}

export default function Profile() {
    const [user, setUser] = useState(null);
    const [form, setForm] = useState({});
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [mpinOpen, setMpinOpen] = useState(false);
    const [mpinForm, setMpinForm] = useState({
        currentMpin: "",
        newMpin: "",
        confirmMpin: "",
    });
    const [mpinError, setMpinError] = useState("");

    useEffect(() => {
        getProfile()
            .then(({ data }) => {
                const mapped = profileToForm(data);
                setUser(mapped);
                setForm(mapped);
            })
            .catch(() => setError("Failed to load profile"))
            .finally(() => setLoading(false));
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const handleSave = async () => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            await updateProfile(form);
            const { data } = await getProfile();
            const mapped = profileToForm(data);
            setUser(mapped);
            setForm(mapped);
            setEditing(false);
            setSuccess("Profile updated successfully");
            if (mapped.firstName)
                localStorage.setItem("firstName", mapped.firstName);
        } catch (e) {
            setError(e.response?.data?.error || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    const handleMpinChange = async () => {
        setMpinError("");
        if (mpinForm.newMpin.length < 4)
            return setMpinError("MPIN must be at least 4 digits");
        if (mpinForm.newMpin !== mpinForm.confirmMpin)
            return setMpinError("MPIN does not match");
        try {
            await changeMpin({
                currentMpin: mpinForm.currentMpin,
                newMpin: mpinForm.newMpin,
            });
            setMpinOpen(false);
            setMpinForm({ currentMpin: "", newMpin: "", confirmMpin: "" });
            setSuccess("MPIN changed successfully");
        } catch (e) {
            setMpinError(e.response?.data?.error || "Failed to change MPIN");
        }
    };

    if (loading)
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress />
            </Box>
        );

    if (!user)
        return (
            <Box sx={{ py: 4 }}>
                <Alert severity="error">
                    Could not load profile. You may need to sign in again.
                </Alert>
            </Box>
        );

    return (
        <Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                flexWrap="wrap"
                gap={2}
            >
                <Box>
                    <Typography variant="h5">My Profile</Typography>
                    <Typography variant="body2" color="text.secondary">
                        View and manage your information
                    </Typography>
                </Box>
                <Stack direction="row" gap={1.5}>
                    <Button
                        variant="outlined"
                        startIcon={<LockResetRoundedIcon />}
                        onClick={() => setMpinOpen(true)}
                        sx={{ borderRadius: 3 }}
                    >
                        Change MPIN
                    </Button>
                    {!editing ? (
                        <Button
                            variant="contained"
                            startIcon={<EditRoundedIcon />}
                            onClick={() => setEditing(true)}
                            sx={{ borderRadius: 3 }}
                        >
                            Edit Profile
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            startIcon={<SaveRoundedIcon />}
                            onClick={handleSave}
                            disabled={saving}
                            sx={{ borderRadius: 3 }}
                        >
                            {saving ? "Saving..." : "Save Changes"}
                        </Button>
                    )}
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                    {success}
                </Alert>
            )}

            {/* Header */}
            <Card sx={{ p: 3, mb: 3 }}>
                <Stack direction="row" alignItems="center" gap={3}>
                    <Avatar
                        sx={{
                            width: 72,
                            height: 72,
                            bgcolor: "primary.main",
                            fontSize: 28,
                            fontWeight: 700,
                        }}
                    >
                        {user?.firstName?.[0]?.toUpperCase()}
                        {user?.lastName?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box>
                        <Typography variant="h5">
                            {user?.firstName} {user?.lastName}
                        </Typography>
                        <Stack direction="row" gap={1} mt={0.5}>
                            <Chip
                                size="small"
                                label={user?.roleName?.replace(/_/g, " ")}
                                color="primary"
                                sx={{ fontWeight: 600 }}
                            />
                            <Chip
                                size="small"
                                label={user?.isActive ? "Active" : "Inactive"}
                                color={user?.isActive ? "success" : "default"}
                            />
                        </Stack>
                    </Box>
                </Stack>
            </Card>

            <Card sx={{ p: { xs: 3, md: 4 } }}>
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={2}
                    color="primary"
                >
                    Personal Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={form.firstName || ""}
                            onChange={set("firstName")}
                            disabled={!editing}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Last Name"
                            value={form.lastName || ""}
                            onChange={set("lastName")}
                            disabled={!editing}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Email"
                            value={form.email || ""}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Mobile"
                            value={form.mobileNumber || ""}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            select
                            label="Gender"
                            value={form.gender || ""}
                            onChange={set("gender")}
                            disabled={!editing}
                        >
                            <MenuItem value="">—</MenuItem>
                            {GENDER_OPTS.map((g) => (
                                <MenuItem key={g} value={g}>
                                    {g}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Date of Birth"
                            type="date"
                            value={form.dateOfBirth?.slice(0, 10) || ""}
                            onChange={set("dateOfBirth")}
                            InputLabelProps={{ shrink: true }}
                            disabled={!editing}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={2}
                    color="primary"
                >
                    Address
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address Line 1"
                            value={form.address1 || ""}
                            onChange={set("address1")}
                            disabled={!editing}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address Line 2"
                            value={form.address2 || ""}
                            onChange={set("address2")}
                            disabled={!editing}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="City"
                            value={form.city || ""}
                            onChange={set("city")}
                            disabled={!editing}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="State"
                            value={form.state || ""}
                            onChange={set("state")}
                            disabled={!editing}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Pincode"
                            value={form.pincode || ""}
                            onChange={set("pincode")}
                            disabled={!editing}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={2}
                    color="primary"
                >
                    KYC Details
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="PAN"
                            value={form.panCardNumber || ""}
                            onChange={set("panCardNumber")}
                            disabled={!editing}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Aadhar"
                            value={form.aadharNumber || ""}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Name on Aadhar"
                            value={form.nameOnAadhar || ""}
                            disabled
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={2}
                    color="primary"
                >
                    Bank Details
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Account Number"
                            value={form.bankAccountNumber || ""}
                            onChange={set("bankAccountNumber")}
                            disabled={!editing}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="IFSC Code"
                            value={form.ifscCode || ""}
                            onChange={set("ifscCode")}
                            disabled={!editing}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={2}
                    color="primary"
                >
                    Limits
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Deposit Limit"
                            value={form.depositLimit || ""}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Daily Withdraw Limit"
                            value={form.withdrawDailyLimit || ""}
                            disabled
                        />
                    </Grid>
                </Grid>
            </Card>

            {/* MPIN Dialog */}
            <Dialog
                open={mpinOpen}
                onClose={() => setMpinOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Change MPIN</DialogTitle>
                <DialogContent>
                    {mpinError && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                            {mpinError}
                        </Alert>
                    )}
                    <Stack spacing={2} mt={1}>
                        <TextField
                            fullWidth
                            label="Current MPIN"
                            type="password"
                            value={mpinForm.currentMpin}
                            onChange={(e) =>
                                setMpinForm({
                                    ...mpinForm,
                                    currentMpin: e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 6),
                                })
                            }
                        />
                        <TextField
                            fullWidth
                            label="New MPIN"
                            type="password"
                            value={mpinForm.newMpin}
                            onChange={(e) =>
                                setMpinForm({
                                    ...mpinForm,
                                    newMpin: e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 6),
                                })
                            }
                        />
                        <TextField
                            fullWidth
                            label="Confirm New MPIN"
                            type="password"
                            value={mpinForm.confirmMpin}
                            onChange={(e) =>
                                setMpinForm({
                                    ...mpinForm,
                                    confirmMpin: e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 6),
                                })
                            }
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setMpinOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleMpinChange}>
                        Change MPIN
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
